import {
  ReservationRoomDTO,
  ReservationWithRateDetailsDTO,
  ReservationOption,
  CheckInSettings,
  FlowStatus,
} from '@/models';
import {
  formatPropertyDate,
  propertyLocalDate,
  utcDatetimeToTimezoneDateString,
} from '@/lib/dates';
import { getLongDateFormat, PropertyDateFormat } from '@/lib/dateFormat';
import { formatInTimeZone } from 'date-fns-tz';

type DateInterval = {
  arrivalDate: string;
  departureDate: string;
};

export const getDateInterval = (
  reservation: ReservationWithRateDetailsDTO,
  timezone: string,
  locale?: Locale,
  propertyDateFormat?: PropertyDateFormat,
): DateInterval => {
  const format = propertyDateFormat
    ? getLongDateFormat(propertyDateFormat)
    : 'P';

  return {
    arrivalDate: formatPropertyDate(
      reservation.startDate,
      timezone,
      format,
      locale,
    ),
    departureDate: formatPropertyDate(
      reservation.endDate,
      timezone,
      format,
      locale,
    ),
  };
};

export const getRoomTitle = (
  room: Pick<ReservationRoomDTO, 'roomTypeName' | 'roomName'> | undefined,
  intl: any,
  displayGuestRoomNumber: boolean = true,
): string => {
  if (!room) return '';

  const notAssigned = intl.formatMessage({
    id: 'common/not_assigned',
    defaultMessage: 'Not assigned',
  });

  return displayGuestRoomNumber
    ? `${room.roomTypeName} - ${room.roomName || notAssigned}`
    : room.roomTypeName;
};

export const getCheckInAvailability = (
  reservation: { startDate: string; status: string },
  daysBeforeArrival: number,
  timezone: string,
) => {
  const today = utcDatetimeToTimezoneDateString(
    new Date().toISOString(),
    timezone,
  );
  const arrivalDate = propertyLocalDate(reservation.startDate, timezone);
  // compared against the property-local 'yyyy-MM-dd' today; day math and
  // formatting stay on the pinned UTC-midnight date so the browser's timezone
  // and its DST changes can't shift the result
  const canCheckInDate = formatInTimeZone(
    arrivalDate.getTime() - daysBeforeArrival * 86400000,
    'UTC',
    'yyyy-MM-dd',
  );

  return {
    canCheckIn: ['confirmed', 'not_confirmed'].includes(reservation.status),
    isTooEarly: today < canCheckInDate,
  };
};

/**
 * Filters reservations to only include those eligible for check-in
 *
 * Criteria:
 * - status === 'confirmed' (reservation is confirmed)
 * - checkInFlowStatus !== 'completed' (not already checked in)
 * - canCheckIn && !isTooEarly (within check-in window based on settings)
 */
export const filterCheckInEligibleReservations = (
  reservations: ReservationOption[],
  checkInSettings: CheckInSettings | null,
  timezone: string,
): ReservationOption[] => {
  // Filter by status and checkInFlowStatus
  const uncheckedReservations = reservations.filter(
    (r) => r.checkInFlowStatus != FlowStatus.COMPLETED,
  );

  // If check-in settings not enabled, return empty
  if (!checkInSettings?.enabled) {
    return [];
  }

  // Further filter by check-in availability window
  return uncheckedReservations.filter((r) => {
    const { canCheckIn, isTooEarly } = getCheckInAvailability(
      r,
      checkInSettings.days_before_arrival,
      timezone,
    );
    return canCheckIn && !isTooEarly;
  });
};
